import React, { type ReactNode } from "react";
import { Building2, Calendar, ArrowRight, Clock, Wallet } from "@semo/icons";
import { cn } from "../../utils";
import { formatDateForDisplay } from "./utils";
import type { QuoteRequestListItemDto } from "@semo/api-contracts";
import { getPaymentChannelLabel } from "./paymentChannel";

export interface ProjectCardHorizontalProps {
  project: QuoteRequestListItemDto;
  onClick?: () => void;
  /** Custom action element to replace the default "상세보기" button */
  action?: ReactNode;
  /**
   * Optional element rendered inside the card at the far left (vertically
   * centered) — e.g. a bookmark/star toggle. Omitted by default so existing
   * consumers render unchanged.
   */
  leadingSlot?: ReactNode;
  className?: string;
}

const STATUS_STYLE_MAP = {
  IN_PROGRESS: "text-primary bg-primary/5 border-primary/20",
  ON_HOLD: "text-orange-600 bg-orange-50 border-orange-200",
  CLOSED: "text-green-600 bg-green-50 border-green-200",
} as const;

export const ProjectCardHorizontal = React.memo(function ProjectCardHorizontal({
  project,
  onClick,
  action,
  leadingSlot,
  className,
}: ProjectCardHorizontalProps) {
  const statusKey =
    project.status === "ON_HOLD"
      ? "ON_HOLD"
      : project.status === "CLOSED"
        ? "CLOSED"
        : "IN_PROGRESS";
  const statusStyle = STATUS_STYLE_MAP[statusKey];
  const statusLabel =
    statusKey === "IN_PROGRESS"
      ? "진행중"
      : statusKey === "ON_HOLD"
        ? "대기"
        : "마감";

  // 발주기관명은 공개 공고에서만 내려온다(비공개 공고·기관명 없는 구매자는 null). 없으면 줄 자체를 빼서
  // 기존 카드 높이를 그대로 유지한다.
  const organizationName = project.organizationName?.trim();

  return (
    <div
      onClick={onClick}
      className={cn(
        "group bg-white border border-gray-100 rounded-2xl p-5 hover:shadow-[0_12px_32px_-4px_rgba(46,127,238,0.12)] hover:border-primary/30 transition-all cursor-pointer flex flex-col sm:flex-row sm:items-center gap-4 sm:gap-6",
        className,
      )}
    >
      {leadingSlot ? (
        <div className="flex-shrink-0 self-center">{leadingSlot}</div>
      ) : null}

      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 mb-1.5">
          <span
            className={cn(
              "px-2 py-0.5 rounded-[6px] text-2xs font-semibold border uppercase tracking-wider",
              statusStyle,
            )}
          >
            {statusLabel}
          </span>
          <span className="text-2xs font-medium text-gray-400">
            {project.requestNumber}
          </span>
        </div>
        <h3 className="font-semibold text-lg text-gray-900 truncate group-hover:text-primary transition-colors">
          {project.projectName || "제목 없음"}
        </h3>
        {organizationName ? (
          <div className="mt-1 flex items-center gap-1.5 min-w-0">
            <Building2 className="w-3.5 h-3.5 text-gray-400 flex-shrink-0" />
            <span
              className="text-sm font-medium text-gray-600 truncate"
              title={organizationName}
            >
              {organizationName}
            </span>
          </div>
        ) : null}
      </div>

      <div className="flex flex-wrap sm:flex-nowrap items-center gap-4 sm:gap-8 text-sm text-gray-500">
        <div className="flex items-center gap-2">
          <Calendar className="w-4 h-4 text-primary/60" />
          <div className="flex flex-col sm:flex-row sm:gap-1.5 items-start sm:items-center">
            <span className="font-medium text-gray-700">
              {formatDateForDisplay(project.deadline)}
            </span>
            <span className="text-2xs text-gray-400">마감</span>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <Clock className="w-4 h-4 text-primary/60" />
          <div className="flex flex-col sm:flex-row sm:gap-1.5 items-start sm:items-center">
            <span className="font-medium text-gray-700">
              {formatDateForDisplay(String(project.createdAt ?? ""))}
            </span>
            <span className="text-2xs text-gray-400">등록</span>
          </div>
        </div>
        {/* 신설(2026-08-19) 이전 공고는 null 이라 아예 안 그린다 — 고른 적 없는 값에
            「미결정」이 붙으면 고른 것처럼 읽힌다. */}
        {getPaymentChannelLabel(project.preferredPaymentChannel) && (
          <div className="flex items-center gap-2">
            <Wallet className="w-4 h-4 text-primary/60" />
            <div className="flex flex-col sm:flex-row sm:gap-1.5 items-start sm:items-center">
              <span className="font-medium text-gray-700">
                {getPaymentChannelLabel(project.preferredPaymentChannel)}
              </span>
              <span className="text-2xs text-gray-400">결제</span>
            </div>
          </div>
        )}
      </div>

      <div className="flex items-center gap-4 pl-0 sm:pl-6 sm:border-l border-gray-100">
        {action ? (
          action
        ) : (
          <button className="flex items-center gap-1.5 px-5 py-2.5 bg-gray-50 text-gray-600 rounded-xl border border-gray-100 text-sm font-semibold hover:bg-primary/5 hover:text-primary hover:border-primary/20 transition-all">
            상세보기
            <ArrowRight className="w-4 h-4" />
          </button>
        )}
      </div>
    </div>
  );
});

ProjectCardHorizontal.displayName = "ProjectCardHorizontal";
